import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { debounce, get } from 'lodash';
import { getPbmBenefit, getPspBenefit, Medicine, OriginEnum } from '../../interfaces/medicine';
import { ExamService } from './examService';
import { MedicineService } from './medicineService';
import { ExamSearch } from '../../interfaces/exam';
import { useLoading } from '../../helpers/useLoading';
import ResultsNotFound from './ResultsNotFound';
import { BsPlusCircle } from 'react-icons/bs';
import { FiSearch } from 'react-icons/fi';
import { toast } from '../../helpers/toast';
import { Tracker } from '../../shared/tracker/Tracker';
import { Benefit, DoctorPermissions } from '../../shared/prescriber';
import SearchTypes, { ItemContent } from './SearchType';
import { PrescriptionContext, PrescriptionContextProps } from '../../providers/PrescriptionProvider';
import { Flex } from '../UI-CHAKRA/Flex';
import { Text } from '../UI-CHAKRA/Text';
import { Checkbox } from '../UI-CHAKRA/Checkbox';
import { Skeleton } from '../UI-CHAKRA/Skeleton';
import { Spinner } from '../UI-CHAKRA/Spinner';
import {
  Fade,
  Input,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  useBreakpointValue,
} from '@chakra-ui/react';
import { ReactComponent as PlusIcon } from '../../assets/images/icons/mevo/plus.svg';
import { InputGroup } from '../UI-CHAKRA/Input/InputGroup';
import { Box } from '../UI-CHAKRA/Box';
import MedicineList from './MedicineList';
import ExamList from './ExamList';
import SearchAlternatives from './SearchAlternatives';
import { useSearchTypeStore } from '../../stores/searchTypeStore';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import { logger } from '../../helpers/logger';
import { SEARCH_TYPE } from './SearchTypeEnum';
import { usePrescriptionLayoutStore } from '../../stores/prescriptionLayoutStore';
import { TrackerEventService } from '../../containers/prescription/trackerEventService';
import { DateTime } from 'luxon';
import { FormulaService } from './formulaService';
import FormulaList from './formulaList';
import { Formula } from '../../interfaces/formula';
import ConfirmDialog from '../../containers/prescription/PrescriptionFormulaContent/components/ConfirmDialog';
import { useIsMobile } from '../../helpers/useIsMobile';
import { amplitudeModule } from '../../shared/amplitude';
import { appViewportHeight } from '../../helpers/embeddedViewport';

interface MedicineSearchProps {
  onSelectMedicine(medicine: Medicine, searchMedicineId: number): void;
  onSelectManualMedicine(manualMedicineName: string): void;
  onSelectExam(params: { exam: ExamSearch; searchExamLogId: number | null }): void;
  prescriptionId: number | null;
  doctorId: number | null;
  benefit: Benefit | null;
  canAddExam: boolean;
  canAddMedicalCertificate: boolean;
  canAddMedicalReferral: boolean;
  onClearEditionAndAdition(): void;
  canFilterRename: boolean;
  canFilterRemume: boolean;
  canAddMedicalReport: boolean;
  searchInputRef: any;
  onChangeItem?: (data: SEARCH_TYPE) => void;
  prescriberId: number | null;
  onSelectFormula: (formula: Formula | null) => void;
  doctorPermissions: DoctorPermissions;
  allPermissionsFalse: boolean;
  hasCRMorCRO: boolean;
  searchBarRightAction?: React.ReactNode;
}

function Search(props: MedicineSearchProps) {
  const [searchMedicineId, setSearchMedicineId] = useState<number>(0);
  const [medicines, setMedicines] = useState<Array<Medicine>>([]);
  const [exams, setExams] = useState<Array<ExamSearch>>([]);
  const [formulas, setFormulas] = useState<Array<Formula>>([]);
  const [formulaPage, setFormulaPage] = useState(1);
  const [formulaPageSize] = useState(20);
  const [hasMoreFormulas, setHasMoreFormulas] = useState(false);
  const [loadingMoreFormulas, setLoadingMoreFormulas] = useState(false);
  const [formulaSearchQuery, setFormulaSearchQuery] = useState('');
  const [isFormulaSelected, setIsFormulaSelected] = useState(false);
  const [hasSearchedOnFocus, setHasSearchedOnFocus] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [alternatives, setAlternatives] = useState<Array<string>>([]);
  const [search, setSearch] = useState('');
  const [showingResultsFor, setShowingResultsFor] = useState<string | null>(null);
  const [showNoResultsFound, setShowNoResultsFound] = useState(false);
  const [allowManual, setAllowManual] = useState(false);
  const [searchExamLogId, setSearchExamLogId] = useState<number>(0);
  const [loading, runWithLoading] = useLoading();
  const medicinePlaceholder = useBreakpointValue(
    {
      base: 'Medicamentos, vacinas, dermocosméticos ou insumos hospitalares.',
      md: 'Busque por medicamentos, vacinas, dermocosméticos ou insumos hospitalares.',
    },
    { ssr: false },
  );

  const [searchTypesContainerStyle, setSearchTypesContainerStyle] = useState<Record<string, string | number> | null>(
    null
  );

  const { prescriptionLoading } = useContext<PrescriptionContextProps>(PrescriptionContext);

  const { prescriberConfiguration, setIsFormulaFormDialogOpen, prescription, isFormulaFormDialogOpen } =
    usePrescriptionStore();

  const prescriber = usePrescriptionStore((store) => store.prescriber());
  const primaryColor = usePrescriptionStore((store) => store.primaryColor());
  const { scrollTop } = usePrescriptionLayoutStore();

  const { searchType: searchBarType, setSearchType: changeSearchBarType } = useSearchTypeStore();

  const mandatoryRemumeSearch = get(prescriberConfiguration, 'Features.enableMandatoryRemumeSearch', false);

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [formulaIdToDelete, setFormulaIdToDelete] = useState<number | null>(null);

  const [filters, setFilters] = useState({
    searchByBenefit: false,
    searchByRename: false,
    searchByRemume: false,
  });

  const {
    onSelectMedicine,
    onSelectManualMedicine,
    onSelectExam,
    prescriptionId,
    doctorId,
    benefit,
    canAddExam,
    canAddMedicalCertificate,
    canAddMedicalReferral,
    onClearEditionAndAdition,
    canAddMedicalReport,
    searchInputRef,
    onChangeItem,
    prescriberId,
    onSelectFormula,
    allPermissionsFalse,
    hasCRMorCRO,
  } = props;

  const canShowPrescription =
    !allPermissionsFalse &&
      !hasCRMorCRO &&
      props.doctorPermissions?.Prescription !== undefined &&
      props.doctorPermissions?.Prescription !== null &&
      typeof props.doctorPermissions?.Prescription === 'boolean'
      ? props.doctorPermissions?.Prescription
      : true;

  const canShowFormula = get(prescriberConfiguration, 'Features.compoundedFormula', false);

  const isMobile = useIsMobile();

  const searchMedicines = useCallback(
    async (params: SearchMedicineParams) => {
      const { search, prescriptionId, onlyWithBenefits, onlyWithRename, onlyWithRemume } = params;
      runWithLoading(async () => {
        try {
          const searchResult = await MedicineService.searchMedicine({
            search: search,
            idPrescricao: prescriptionId,
            onlyWithBenefits,
            onlyWithRename,
            onlyWithRemume,
          });

          Tracker.trackEvent({
            event: 'Medication Searched',
            parameters: {
              searchedValue: search,
            },
          });

          const premiumMedicineIds = searchResult.Itens.filter((medicine) => medicine.isPremiumPlacement).map(
            (item) => item.IdMedicamento
          );

          if (premiumMedicineIds.length > 0) {
            await TrackerEventService.track(prescriptionId, 'Item_patrocinado_exibido_na_busca', {
              idPrescricao: prescriptionId,
              idMedico: prescriber?.idPrescritor,
              idMedicamento: premiumMedicineIds,
              termoBuscado: search,
            });
          }

          const CTAmedicineIds = searchResult.Itens.filter((medicine) => medicine.ctaText).map(
            (item) => item.IdMedicamento
          );

          if (CTAmedicineIds.length > 0) {
            await TrackerEventService.track(prescriptionId, 'Backoffice_campaign_cta_busca_exibiu', {
              idPrescricao: prescriptionId,
              idMedico: prescriber?.idPrescritor,
              idMedicamento: CTAmedicineIds,
              termoBuscado: search,
              timeStamp: DateTime.now(),
            });
          }

          setExams([]);
          setMedicines(searchResult.Itens);
          setShowOptions(true);
          setShowNoResultsFound(
            (searchResult.Alternativas || []).length + searchResult.Itens.length === 0 || !searchResult.Itens
          );

          setShowingResultsFor(searchResult.MostrandoResultadosPara || '');
          setAlternatives(
            (searchResult.Alternativas || []).filter(
              (alternative) => alternative !== searchResult.MostrandoResultadosPara
            )
          );
          setSearchMedicineId(searchResult.idBuscaMedicamento);
          setAllowManual(searchResult.PermitirMedicamentoManual);

          const enablePspBenefit = get(prescriberConfiguration, 'Features.enablePspBenefit', false);
          if (enablePspBenefit && prescriptionId && prescription) {
            const tracked = new Set<number>();
            for (const medicine of searchResult.Itens) {
              const pspBenefit = getPspBenefit(medicine.Beneficios);
              if (pspBenefit && !tracked.has(medicine.IdMedicamento)) {
                tracked.add(medicine.IdMedicamento);
                TrackerEventService.track(prescriptionId, 'backoffice_campaign_psp_tag_medicamento_view', {
                  flow: 'selecionar medicamento',
                  idPrescricao: prescriptionId,
                  codigoValidacao: prescription?.CodigoValidacao,
                  idParceiro: prescriberConfiguration?.idParceiro,
                  idMedico: prescriber?.idPrescritor,
                  idPaciente: prescription?.Paciente?.idPaciente,
                  idMedicamento: medicine.IdMedicamento,
                  listaEans: medicine?.EANs ?? medicine?.ListaEANS ?? [],
                  nomeDoPrograma: pspBenefit.Nome,
                  isMobile: isMobile,
                  tipoDePrograma: 'PSP',
                  ambiente: process.env.REACT_APP_ENVIRONMENT || 'staging',
                  termoBuscado: search,
                });
              }
            }
          }
          const enablePbmBenefit = get(prescriberConfiguration, 'Features.enablePbmBenefit', false);
          if (enablePbmBenefit && prescriptionId && prescription) {
            const trackedPbm = new Set<number>();
            for (const medicine of searchResult.Itens) {
              const pbmBenefit = getPbmBenefit(medicine.Beneficios);
              if (pbmBenefit && !trackedPbm.has(medicine.IdMedicamento)) {
                trackedPbm.add(medicine.IdMedicamento);
                TrackerEventService.track(prescriptionId, 'backoffice_campaign_pbm_tag_medicamento_view', {
                  flow: 'selecionar medicamento',
                  idPrescricao: prescriptionId,
                  codigoValidacao: prescription?.CodigoValidacao,
                  idParceiro: prescriberConfiguration?.idParceiro,
                  idMedico: prescriber?.idPrescritor,
                  idPaciente: prescription?.Paciente?.idPaciente,
                  idMedicamento: medicine.IdMedicamento,
                  listaEans: medicine?.EANs ?? medicine?.ListaEANS ?? [],
                  nomeDoPrograma: pbmBenefit.Nome,
                  isMobile: isMobile,
                  tipoDePrograma: 'PBM',
                  termoBuscado: search,
                });
              }
            }
          }
        } catch (e: any) {
          if (e.isAxiosError) {
            setAllowManual(true);
            setShowOptions(true);
            setShowNoResultsFound(true);

            return;
          }

          toast.error('Erro ao buscar medicamentos.');
        }
      });
    },
    [prescriber?.idPrescritor, runWithLoading, isMobile, prescriberConfiguration, prescription]
  );

  const searchExams = useCallback(
    async ({ prescriptionId, search, doctorId }: { search: string; prescriptionId: number; doctorId: number }) => {
      runWithLoading(async () => {
        try {
          const { exams, searchExamLogId } = await ExamService.searchExam({ search, prescriptionId, doctorId });
          if (searchExamLogId) {
            setSearchExamLogId(searchExamLogId);
          }
          setMedicines([]);
          setExams(exams);
          setShowOptions(true);
          setShowNoResultsFound(exams.length === 0);
        } catch (e) {
          logger.error(e);
          toast.error('Erro ao buscar exames.');
        }
      });
    },
    [runWithLoading]
  );

  const debouncedSearchMedicines = useMemo<(params: SearchMedicineParams) => void>(
    () =>
      debounce((params: SearchMedicineParams) => {
        if (!params.search) return cleanSearch();
        if (params.search.length < 3) return;
        return searchMedicines(params);
      }, 500),
    [searchMedicines]
  );

  const debouncedSearchExams = useMemo(
    () =>
      debounce(({ doctorId, prescriptionId, search }: { search: string; prescriptionId: number; doctorId: number }) => {
        if (!search) return cleanSearch();
        if (search.length < 2) return;
        searchExams({
          search,
          prescriptionId,
          doctorId,
        });
      }, 500),
    [searchExams]
  );

  const searchFormulas = useCallback(
    async ({
      doctorId,
      search,
      page,
      append = false,
    }: {
      search: string;
      doctorId: number;
      page: number;
      append?: boolean;
    }) => {
      setLoadingMoreFormulas(true);
      try {
        const data = await FormulaService.searchFormula(doctorId, search, page, formulaPageSize);

        if (append) {
          setFormulas((prev) => [...prev, ...data.formulas]);
        } else {
          setFormulas(data.formulas);
        }
        const totalPages = Math.ceil(data.count / formulaPageSize);
        setHasMoreFormulas(page < totalPages);
        setShowOptions(true);
        setShowNoResultsFound(data.formulas.length === 0 && !append);
      } catch (error) {
        logger.error('Erro ao buscar fórmulas:', error);
        if (!append) {
          setFormulas([]);
          setShowNoResultsFound(true);
        }
      } finally {
        setLoadingMoreFormulas(false);
      }
    },
    [formulaPageSize]
  );

  const debouncedSearchFormulas = useMemo(
    () =>
      debounce(async ({ doctorId, search }: { search: string; doctorId: number }) => {
        // Executa busca mesmo sem caracteres (busca vazia)
        setFormulaPage(1);
        setFormulaSearchQuery(search || '');
        await searchFormulas({
          doctorId,
          search: search || '',
          page: 1,
          append: false,
        });
      }, 500), //milliseconds
    [searchFormulas]
  );

  const loadMoreFormulas = useCallback(() => {
    if (!hasMoreFormulas || loadingMoreFormulas || !doctorId) return;

    const nextPage = formulaPage + 1;
    setFormulaPage(nextPage);
    searchFormulas({
      doctorId,
      search: formulaSearchQuery || '',
      page: nextPage,
      append: true,
    });
  }, [hasMoreFormulas, loadingMoreFormulas, doctorId, formulaSearchQuery, formulaPage, searchFormulas]);

  const selectAlternative = (alternative: string) => {
    setSearch(alternative);
    if (!prescriptionId) return;
    debouncedSearchMedicines({
      search: alternative,
      prescriptionId,
      onlyWithBenefits: filters.searchByBenefit,
      onlyWithRemume: filters.searchByRemume,
      onlyWithRename: filters.searchByRename,
    });
  };

  const doSearch = useCallback(
    (value: string, searchType: string) => {
      setSearch(value);
      setShowNoResultsFound(false);
      setIsFormulaSelected(false);
      setHasSearchedOnFocus(false);

      if (!prescriptionId || !doctorId) return;
      setAllowManual(false);

      switch (searchType) {
        case SEARCH_TYPE.EXAMS: {
          debouncedSearchExams({
            search: value,
            prescriptionId,
            doctorId,
          });
          break;
        }
        case SEARCH_TYPE.MEDICINE: {
          debouncedSearchMedicines({
            search: value,
            prescriptionId,
            onlyWithBenefits: filters.searchByBenefit,
            onlyWithRemume: filters.searchByRemume,
            onlyWithRename: filters.searchByRename,
          });
          break;
        }
        case SEARCH_TYPE.FORMULA: {
          // Não executa busca se o dialog está aberto
          if (isFormulaFormDialogOpen) {
            return;
          }
          setShowOptions(true);
          setHasSearchedOnFocus(true);
          debouncedSearchFormulas({
            search: value,
            doctorId,
          });
          break;
        }
      }
    },
    [
      debouncedSearchExams,
      debouncedSearchFormulas,
      debouncedSearchMedicines,
      filters.searchByBenefit,
      filters.searchByRemume,
      filters.searchByRename,
      prescriptionId,
      doctorId,
      isFormulaFormDialogOpen,
    ]
  );

  const onInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.persist();
      doSearch(event.target.value, searchBarType);
    },
    [doSearch, searchBarType]
  );

  const handleInputFocus = useCallback(() => {
    // Não executa busca se o dialog está aberto, se uma fórmula já foi selecionada, ou se já está carregando
    if (
      searchBarType === SEARCH_TYPE.FORMULA &&
      !isFormulaSelected &&
      !isFormulaFormDialogOpen &&
      !loadingMoreFormulas
    ) {
      setShowOptions(true);

      // Executa busca vazia ao focar/clicar no campo apenas se ainda não foi feita uma busca neste foco
      // e se a query não mudou
      if (doctorId && !hasSearchedOnFocus) {
        setHasSearchedOnFocus(true);
        setFormulaPage(1);
        setFormulaSearchQuery(search || '');
        searchFormulas({
          doctorId,
          search: search || '',
          page: 1,
          append: false,
        });
      }
    }
  }, [
    searchBarType,
    search,
    doctorId,
    searchFormulas,
    isFormulaSelected,
    hasSearchedOnFocus,
    isFormulaFormDialogOpen,
    loadingMoreFormulas,
  ]);

  useEffect(() => {
    if (![SEARCH_TYPE.MEDICINE, SEARCH_TYPE.EXAMS, SEARCH_TYPE.FORMULA].includes(searchBarType)) {
      return;
    }

    // Para fórmulas, não executa busca automática ao mudar de aba
    // A busca só acontece quando o usuário interage com o campo (foco/clique ou digitação)
    if (searchBarType === SEARCH_TYPE.FORMULA) {
      return;
    }

    doSearch(search, searchBarType);
  }, [doSearch, search, searchBarType]);

  // Reseta flags quando o dialog de fórmula fecha
  useEffect(() => {
    if (!isFormulaFormDialogOpen && searchBarType === SEARCH_TYPE.FORMULA) {
      setIsFormulaSelected(false);
      setHasSearchedOnFocus(false);
    }
  }, [isFormulaFormDialogOpen, searchBarType]);

  const onChangeSearchType = useCallback(
    (item: ItemContent) => {
      onClearEditionAndAdition();

      // Limpa resultados de outros tipos quando muda para Fórmulas
      if (item.type === SEARCH_TYPE.FORMULA) {
        setMedicines([]);
        setExams([]);
        setShowOptions(false);
        setShowNoResultsFound(false);
        setIsFormulaSelected(false);
        setHasSearchedOnFocus(false);
      }

      changeSearchBarType(item.type);
      if (item.searchable) {
        Tracker.trackEvent({
          event: 'Search Type',
          parameters: {
            'Search Type': item.type,
          },
        });

        return;
      }
      if (onChangeItem) onChangeItem(item.type);
    },
    [onClearEditionAndAdition, changeSearchBarType, onChangeItem]
  );

  const onSelectMedicineSearch = useCallback(
    async (medicine: Medicine) => {
      if (!prescriptionId) return;

      if (medicine.isPremiumPlacement) {
        await TrackerEventService.track(prescriptionId, 'CTR_item_patrocinado', {
          idPrescricao: prescriptionId,
          idMedico: prescriberId,
          idMedicamento: medicine.IdMedicamento,
          termoBuscado: search,
        });
      }

      if (medicine.ctaText) {
        await TrackerEventService.track(prescriptionId, 'Backoffice_campaign_cta_busca_selecionado', {
          idPrescricao: prescriptionId,
          idMedico: prescriberId,
          idMedicamento: medicine.IdMedicamento,
          termoBuscado: search,
          timeStamp: DateTime.now(),
        });
      }

      amplitudeModule.searchResultClicked({
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: prescription?.Estabelecimento?.Nome ?? '',
        validation_code: prescription?.CodigoValidacao ?? '',
        prescription_id: prescription?.idPrescricao ?? 0,
        screen_name: 'busca',
        search_type: 'Medicamento',
        search_term: search,
        medication_id: medicine.IdMedicamento ?? 0,
        medication_name: medicine.Nome ?? medicine.Label ?? '',
        medication_quantity: medicine.Quantidade ?? 0,
        medication_is_controlled: medicine.ControleEspecial ?? false,
        medication_is_sponsored: medicine.isPremiumPlacement ?? false,
        medication_dosage_form: medicine.FormaFarmaceutica ?? '',
        medication_has_psp: !!getPspBenefit(medicine.Beneficios),
        medication_is_LME: medicine.LME ?? false,
        medication_is_MIP: medicine.MIP ?? false,
        medication_is_active_ingredients: (medicine.PrincipiosAtivos?.length ?? 0) > 0,
        medication_price_range: medicine.MenorPreco ?? 0,
      });

      const enablePspBenefit = get(prescriberConfiguration, 'Features.enablePspBenefit', false);
      const pspBenefit = getPspBenefit(medicine.Beneficios);
      const hasPspBenefit = !!pspBenefit;
      if (hasPspBenefit && enablePspBenefit) {
        await TrackerEventService.track(prescriptionId, 'backoffice_campaign_psp_tag_medicamento_click', {
          flow: 'selecionar medicamento',
          idPrescricao: prescriptionId,
          codigoValidacao: prescription?.CodigoValidacao,
          idParceiro: prescriberConfiguration?.idParceiro,
          idMedico: prescriber?.idPrescritor,
          idPaciente: prescription?.Paciente?.idPaciente,
          idMedicamento: medicine.IdMedicamento,
          listaEans: medicine.EANs ?? medicine.ListaEANS ?? [],
          nomeDoPrograma: pspBenefit?.Nome,
          isMobile: isMobile,
          tipoDePrograma: 'PSP',
          ambiente: process.env.REACT_APP_ENVIRONMENT || 'staging',
        });
      }

      const enablePbmBenefit = get(prescriberConfiguration, 'Features.enablePbmBenefit', false);
      const pbmBenefit = getPbmBenefit(medicine.Beneficios);
      const hasPbmBenefit = !!pbmBenefit;
      if (hasPbmBenefit && enablePbmBenefit) {
        await TrackerEventService.track(prescriptionId, 'backoffice_campaign_pbm_tag_medicamento_click', {
          flow: 'selecionar medicamento',
          idPrescricao: prescriptionId,
          codigoValidacao: prescription?.CodigoValidacao,
          idParceiro: prescriberConfiguration?.idParceiro,
          idMedico: prescriber?.idPrescritor,
          idPaciente: prescription?.Paciente?.idPaciente,
          idMedicamento: medicine.IdMedicamento,
          listaEans: medicine.EANs ?? medicine.ListaEANS ?? [],
          nomeDoPrograma: pbmBenefit?.Nome,
          isMobile: isMobile,
          tipoDePrograma: 'PBM',
        });
      }

      setSearch('');
      setShowOptions(false);
      onSelectMedicine(medicine, searchMedicineId);
    },
    [
      onSelectMedicine,
      prescriberId,
      prescriptionId,
      search,
      searchMedicineId,
      prescriberConfiguration,
      prescriber,
      prescription,
      isMobile,
    ]
  );

  const onSelectExamSearch = useCallback(
    (exam: ExamSearch) => {
      amplitudeModule.searchResultClicked({
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: prescription?.Estabelecimento?.Nome ?? '',
        validation_code: prescription?.CodigoValidacao ?? '',
        prescription_id: prescription?.idPrescricao ?? 0,
        screen_name: 'busca',
        search_type: 'Exame',
        search_term: search,
        medical_test_id: exam.idEzOrder ?? 0,
        medical_test_name: exam.Nome ?? exam.Label ?? '',
      });

      setSearch('');
      setShowOptions(false);
      onSelectExam({ exam, searchExamLogId });
      searchInputRef.current.focus();
    },
    [onSelectExam, searchExamLogId, searchInputRef, search, prescriberConfiguration, prescription]
  );

  const cleanSearch = () => {
    setSearch('');
    setExams([]);
    setMedicines([]);
    setFormulas([]);
    setShowOptions(false);
    setShowNoResultsFound(false);
    setAlternatives([]);
    setFormulaPage(1);
    setHasMoreFormulas(false);
    setFormulaSearchQuery('');
    setIsFormulaSelected(false);
    setHasSearchedOnFocus(false);
  };

  const onSelectManualMedicineSearch = () => {
    const treatedManualMedicine = search.substring(0, 600);

    amplitudeModule.manualMedicationClicked({
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
      establishment_name: prescription?.Estabelecimento?.Nome ?? '',
    });

    onSelectManualMedicine(treatedManualMedicine);
    cleanSearch();
  };

  const showAlternativesBox = useMemo(() => {
    if (searchBarType !== SEARCH_TYPE.MEDICINE && searchBarType !== SEARCH_TYPE.FORMULA) return false;

    return alternatives.length > 0 || showingResultsFor;
  }, [alternatives.length, searchBarType, showingResultsFor]);

  /**
   * Como possuímos 3 filtros diferentes, esse método atualiza algum dos campos de filtro possíveis,
   * dessa forma podemos utilizar apenas um método
   * Regras:
   * 1. No campo do filtro o atributo "name" precisa corresponder a uma chave no objeto de filtros
   */
  const onChangeSearchFilter = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      const targetName = event.target.name;
      const targetIsChecked = event.target.checked;
      const newFilters = {
        ...filters,
        [targetName]: targetIsChecked,
      };

      setFilters((prevState) => {
        return {
          ...prevState,
          [targetName]: targetIsChecked,
        };
      });

      if (prescriptionId && search.length > 2) {
        debouncedSearchMedicines({
          search,
          prescriptionId,
          onlyWithBenefits: newFilters.searchByBenefit,
          onlyWithRename: newFilters.searchByRename,
          onlyWithRemume: newFilters.searchByRemume,
        });
      }
    },
    [debouncedSearchMedicines, filters, prescriptionId, search]
  );

  const handleOpenConfirmDialog = (formulaId: number) => {
    setFormulaIdToDelete(formulaId);
    setShowOptions(false);
    setIsConfirmDialogOpen(true);
  };

  const handleDeleteFormula = async (formulaId: number) => {
    if (!doctorId) return;
    await FormulaService.deleteFormula(doctorId, formulaId);
    toast.success('Fórmula removida com sucesso');
    setIsConfirmDialogOpen(false);
    setIsFormulaFormDialogOpen(false);

    // Remove a fórmula da lista local
    setFormulas((prev) => prev.filter((formula) => formula.id !== formulaId));

    // Se houver uma busca ativa, refaz a busca para atualizar a lista
    if (formulaSearchQuery && doctorId) {
      await searchFormulas({
        doctorId,
        search: formulaSearchQuery,
        page: 1,
        append: false,
      });
    }
  };

  const handleOpenFormulaFormDialog = useCallback(() => {
    onSelectFormula(null);
    setShowOptions(false);
    setIsFormulaFormDialogOpen(true);
    setIsFormulaSelected(false);
    setHasSearchedOnFocus(false);
  }, [setIsFormulaFormDialogOpen, setShowOptions, onSelectFormula]);

  const Filters = useMemo(() => {
    const canFilterRemume = props.canFilterRemume && searchBarType === SEARCH_TYPE.MEDICINE;
    const canFilterRename = props.canFilterRename && searchBarType === SEARCH_TYPE.MEDICINE;

    if (!canFilterRename && !canFilterRemume && !benefit) return;

    return (
      search.length > 0 && (
        <Flex alignItems={'center'} justifyContent={'flex-start'} p={'10px 20px'} gap={'20px'}>
          {canFilterRename && (
            <Checkbox
              fontWeight={600}
              isChecked={filters.searchByRename}
              name={'searchByRename'}
              onChange={onChangeSearchFilter}
              borderColor={'var(--color-gray-medium)'}
              size={'sm'}
            >
              <Text color={'var(--color-gray-medium)'} fontSize={'12px'}>
                Rename
              </Text>
            </Checkbox>
          )}

          {canFilterRemume && (
            <Checkbox
              fontSize={'10px'}
              fontWeight={600}
              isChecked={filters.searchByRemume}
              name={'searchByRemume'}
              onChange={onChangeSearchFilter}
              borderColor={'var(--color-gray-medium)'}
              size={'sm'}
              isDisabled={mandatoryRemumeSearch}
            >
              <Text color={'var(--color-gray-medium)'} fontSize={'12px'}>
                Remume
              </Text>
            </Checkbox>
          )}

          {benefit && (
            <Checkbox
              fontSize={'10px'}
              fontWeight={600}
              isChecked={filters.searchByBenefit}
              name={'searchByBenefit'}
              onChange={onChangeSearchFilter}
              borderColor={'var(--color-gray-medium)'}
              size={'sm'}
            >
              <Text color={'var(--color-gray-medium)'} fontSize={'12px'}>
                {benefit.NomeBeneficio}
              </Text>
            </Checkbox>
          )}
        </Flex>
      )
    );
  }, [
    props.canFilterRemume,
    props.canFilterRename,
    searchBarType,
    benefit,
    search.length,
    filters.searchByRename,
    filters.searchByRemume,
    filters.searchByBenefit,
    onChangeSearchFilter,
    mandatoryRemumeSearch,
  ]);

  const FormulaInputContainer = useMemo(() => {
    if (prescriptionLoading) {
      return (
        <div>
          <Skeleton height={'68px'} width={'100%'} variant={'rect'} marginBottom={'4px'} borderRadius={'20px'} />
        </div>
      );
    }

    return (
      <div className="flex flex-row items-center px-[14px] h-12 min-h-[48px] bg-white border border-gray-light rounded-[24px] min-w-0 overflow-hidden">
        <div className="mr-2.5 flex items-center justify-center shrink-0">
          {loading ? (
            <Spinner color={primaryColor} minH={'18px'} minW={'18px'} />
          ) : (
            <FiSearch color={primaryColor} size={20} strokeWidth={1.5} />
          )}
        </div>
        <InputGroup flex={1} minW={0}>
          <Input
            p={'0'}
            textOverflow={'ellipsis'}
            whiteSpace={'nowrap'}
            overflow={'hidden'}
            value={search}
            onInput={onInput}
            onFocus={handleInputFocus}
            placeholder={'Buscar fórmulas'}
            ref={searchInputRef}
            border={'none'}
            autoComplete={'off'}
            data-tut={'search-field'}
            sx={{
              '&::placeholder': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          />
        </InputGroup>
      </div>
    );
  }, [prescriptionLoading, loading, search, onInput, searchInputRef, handleInputFocus, primaryColor]);

  const SearchContainer = useMemo(() => {
    if (prescriptionLoading) {
      return (
        <div>
          <Skeleton height={'68px'} width={'100%'} variant={'rect'} marginBottom={'4px'} borderRadius={'20px'} />
        </div>
      );
    }

    if (searchBarType !== SEARCH_TYPE.MEDICINE && prescription?.Origem?.OrigemParceiro === OriginEnum.MEDFLOW)
      return <Box></Box>;

    const renderRightAction = props.searchBarRightAction ? (
      <Box flexShrink={0}>{props.searchBarRightAction}</Box>
    ) : null;

    return searchBarType === SEARCH_TYPE.FORMULA ? (
      // Layout em Tailwind (não Chakra): CSS estático + breakpoint `md` em px (768px),
      // no lugar do flexDirection responsivo do Chakra (em `em`) — que no engine do MV
      // não aplicava o `row` e empilhava input e botão. Input/Popover seguem em Chakra.
      <div className="flex flex-col md:flex-row items-center gap-2.5 my-3">
        <div className="flex-none md:flex-1 w-full md:w-auto min-w-0">
          {FormulaInputContainer}
        </div>
        <button
          type="button"
          onClick={handleOpenFormulaFormDialog}
          className="flex items-center justify-center gap-2 whitespace-nowrap bg-transparent text-primary border-2 border-solid border-primary rounded-[48px] px-4 h-10 w-full md:w-auto cursor-pointer text-base font-semibold"
        >
          <PlusIcon />
          Prescrever fórmula
        </button>
      </div>
    ) : (
      <div className="flex flex-row items-center gap-2.5 my-3">
        <div className="flex flex-row items-center px-[14px] h-12 min-h-[48px] flex-1 bg-white border border-gray-light rounded-[24px] min-w-0 overflow-hidden">
          <div className="mr-2.5 flex items-center justify-center shrink-0">
            {loading ? (
              <Spinner color={primaryColor} minH={'18px'} minW={'18px'} />
            ) : (
              <FiSearch color={primaryColor} size={20} strokeWidth={1.5} />
            )}
          </div>
          <InputGroup flex={1} minW={0}>
            <Input
              p={'0'}
              textOverflow={'ellipsis'}
              whiteSpace={'nowrap'}
              overflow={'hidden'}
              value={search}
              onInput={onInput}
              placeholder={searchBarType === SEARCH_TYPE.MEDICINE ? medicinePlaceholder : 'Buscar exames'}
              autoFocus
              ref={searchInputRef}
              border={'none'}
              autoComplete={'off'}
              data-tut={'search-field'}
              sx={{
                '&::placeholder': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </InputGroup>
        </div>
        {renderRightAction}
      </div>
    );
  }, [
    prescriptionLoading,
    searchBarType,
    onInput,
    search,
    loading,
    searchInputRef,
    prescription?.Origem?.OrigemParceiro,
    FormulaInputContainer,
    handleOpenFormulaFormDialog,
    primaryColor,
    props.searchBarRightAction,
    medicinePlaceholder,
  ]);

  useEffect(() => {
    if (scrollTop > 70) {
      setSearchTypesContainerStyle({
        boxShadow: '0px 4px 10px 0px rgba(0,0,0,0.08)',
      });
      return;
    }
    setSearchTypesContainerStyle(null);
  }, [scrollTop]);

  useEffect(() => {
    if (!mandatoryRemumeSearch) return;

    setFilters({
      searchByBenefit: false,
      searchByRename: false,
      searchByRemume: true,
    });
  }, [mandatoryRemumeSearch]);

  return (
    <>
      <Box
        {...searchTypesContainerStyle}
        transition={'box-shadow 0.2s'}
        position={'sticky'}
        top={0}
        zIndex={2}
        bg={'#f8f9fa'}
        pb={'4px'}
      >
        <Box m={'16px'} mb={0}>
          <SearchTypes
            onChange={onChangeSearchType}
            canAddExam={canAddExam}
            canAddMedicalCertificate={canAddMedicalCertificate}
            canAddMedicalReferral={canAddMedicalReferral}
            selectedSearchType={searchBarType}
            primaryColor={primaryColor}
            canAddMedicalReport={canAddMedicalReport}
            canShowMedicalPrescription={canShowPrescription}
            canShowFormula={canShowFormula}
            canShowPrescription={canShowPrescription}
          />
        </Box>

        <Box id="MedicineSearch" padding={'0 20px'} mt={'16px'}>
          <Box>
            {searchBarType === SEARCH_TYPE.FORMULA ? (
              <div className="flex flex-col md:flex-row items-stretch gap-2.5 my-3">
                <div className="flex-none md:flex-1 w-full md:w-auto min-w-0">
                  <Popover
                    isOpen={showOptions}
                    flip={false}
                    placement="bottom-start"
                    matchWidth
                    isLazy
                    closeOnBlur={false}
                    autoFocus={false}
                  >
                    {({ isOpen }) => (
                      <>
                        <PopoverAnchor>{FormulaInputContainer}</PopoverAnchor>
                        <Flex
                          position={'fixed'}
                          top={0}
                          right={0}
                          bottom={0}
                          left={0}
                          zIndex={10}
                          tabIndex={-1}
                          onClick={() => setShowOptions(false)}
                          display={isOpen ? 'flex' : 'none'}
                        />

                        <PopoverContent
                          w={'100%'}
                          outline={0}
                          variants={{
                            exit: { opacity: 0 },
                            enter: { opacity: 1 },
                          }}
                          boxShadow={'0px 34px 94px 0px rgba(0, 0, 0, 0.10)'}
                          borderRadius={'10px'}
                          overflow={'hidden'}
                          border={'none'}
                        >
                          <PopoverHeader border={'none'} bg={'gray.lightest'} p={0} borderRadius={'10px 10px 0 0'}>
                            {showAlternativesBox && !showNoResultsFound && (
                              <Flex tabIndex={-1} aria-disabled flexDirection={'column'} p={'10px 20px'} gap={'10px'}>
                                {showingResultsFor && (
                                  <Box>
                                    <Text color={'var(--color-gray-medium)'} fontSize={'12px'} fontWeight={600}>
                                      {'Mostrando resultados para '}

                                      <SearchAlternatives
                                        onClick={() => selectAlternative(showingResultsFor)}
                                        alternative={showingResultsFor.toUpperCase()}
                                      />
                                    </Text>
                                  </Box>
                                )}
                                {alternatives.length > 0 && (
                                  <Box>
                                    <Text color={'var(--color-gray-medium)'} fontSize={'12px'} fontWeight={600}>
                                      {'Ou você quis dizer: '}

                                      {alternatives.map((alternative, index) => (
                                        <>
                                          <SearchAlternatives
                                            key={alternative}
                                            onClick={() => selectAlternative(alternative)}
                                            alternative={'alternative'.toUpperCase()}
                                          />
                                          {index + 1 < alternatives.length ? ' ou ' : ''}
                                        </>
                                      ))}
                                    </Text>
                                  </Box>
                                )}
                              </Flex>
                            )}
                            {Filters}
                          </PopoverHeader>
                          <PopoverBody p={0}>
                            <Fade in={showNoResultsFound} unmountOnExit>
                              <ResultsNotFound
                                searchType={searchBarType}
                                showIncludeManualMedicine={allowManual && search !== ''}
                                searchValue={search}
                                onSelectManualMedicineSearch={onSelectManualMedicineSearch}
                                mandatoryRemumeSearch={mandatoryRemumeSearch}
                                openFormulaFormDialog={handleOpenFormulaFormDialog}
                              />
                            </Fade>
                            <Box
                              id="MenuList"
                              maxHeight={appViewportHeight(0.4)}
                              overflow={'auto'}
                              onScroll={(e) => {
                                const target = e.target as HTMLElement;
                                const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
                                // Carrega mais quando está a 50px do final
                                if (scrollBottom < 50 && hasMoreFormulas && !loadingMoreFormulas) {
                                  loadMoreFormulas();
                                }
                              }}
                            >
                              <FormulaList
                                formulas={formulas}
                                onSelectFormula={(formula) => {
                                  amplitudeModule.searchResultClicked({
                                    partner_id: prescriberConfiguration?.idParceiro ?? 0,
                                    partner_name: prescriberConfiguration?.Nome ?? '',
                                    establishment_name: prescription?.Estabelecimento?.Nome ?? '',
                                    validation_code: prescription?.CodigoValidacao ?? '',
                                    prescription_id: prescription?.idPrescricao ?? 0,
                                    screen_name: 'busca',
                                    search_type: 'Fórmula',
                                    search_term: search,
                                    compounded_medication_id: formula?.id ?? 0,
                                    compounded_medication_name: formula?.formulaName ?? '',
                                  });
                                  onSelectFormula(formula);
                                  setShowOptions(false);
                                  setIsFormulaSelected(true);
                                  setHasSearchedOnFocus(false);
                                  setSearch('');
                                  // Fecha o popover imediatamente para evitar que o foco volte ao campo
                                  if (searchInputRef.current) {
                                    searchInputRef.current.blur();
                                  }
                                }}
                                onOpenConfirmDialog={handleOpenConfirmDialog}
                              />
                              {loadingMoreFormulas && (
                                <Flex justifyContent={'center'} p={4}>
                                  <Spinner size="sm" />
                                </Flex>
                              )}
                            </Box>
                          </PopoverBody>
                        </PopoverContent>
                      </>
                    )}
                  </Popover>
                </div>
                <button
                  type="button"
                  onClick={() => setIsFormulaFormDialogOpen(true)}
                  className="flex items-center gap-2 whitespace-nowrap bg-transparent text-primary border-[1.5px] border-solid border-primary rounded-[40px] h-10 px-3 text-sm font-bold w-full md:w-auto self-stretch md:self-center mb-2.5 md:mb-0 cursor-pointer"
                  style={{ fontFamily: 'Ambit-Regular, Inter, sans-serif' }}
                >
                  <PlusIcon />
                  Prescrever fórmula
                </button>
              </div>
            ) : (
              <Popover
                isOpen={showOptions}
                flip={false}
                placement="bottom-start"
                matchWidth
                isLazy
                closeOnBlur={false}
                autoFocus={false}
              >
                {({ isOpen }) => (
                  <>
                    <PopoverAnchor>{SearchContainer}</PopoverAnchor>
                    <Flex
                      position={'fixed'}
                      top={0}
                      right={0}
                      bottom={0}
                      left={0}
                      zIndex={10}
                      tabIndex={-1}
                      onClick={() => setShowOptions(false)}
                      display={isOpen ? 'flex' : 'none'}
                    />

                    <PopoverContent
                      w={'100%'}
                      outline={0}
                      variants={{
                        exit: { opacity: 0 },
                        enter: { opacity: 1 },
                      }}
                      boxShadow={'0px 34px 94px 0px rgba(0, 0, 0, 0.10)'}
                      borderRadius={'10px'}
                      overflow={'hidden'}
                      border={'none'}
                    >
                      <PopoverHeader border={'none'} bg={'gray.lightest'} p={0} borderRadius={'10px 10px 0 0'}>
                        {showAlternativesBox && !showNoResultsFound && (
                          <Flex tabIndex={-1} aria-disabled flexDirection={'column'} p={'10px 20px'} gap={'10px'}>
                            {showingResultsFor && (
                              <Box>
                                <Text color={'#868E96'} fontSize={'12px'} fontWeight={600}>
                                  {'Mostrando resultados para '}

                                  <SearchAlternatives
                                    onClick={() => selectAlternative(showingResultsFor)}
                                    alternative={showingResultsFor.toUpperCase()}
                                  />
                                </Text>
                              </Box>
                            )}
                            {alternatives.length > 0 && (
                              <Box>
                                <Text color={'#868E96'} fontSize={'12px'} fontWeight={600}>
                                  {'Ou você quis dizer: '}

                                  {alternatives.map((alternative, index) => (
                                    <>
                                      <SearchAlternatives
                                        key={alternative}
                                        onClick={() => selectAlternative(alternative)}
                                        alternative={'alternative'.toUpperCase()}
                                      />
                                      {index + 1 < alternatives.length ? ' ou ' : ''}
                                    </>
                                  ))}
                                </Text>
                              </Box>
                            )}
                          </Flex>
                        )}
                        {Filters}
                      </PopoverHeader>
                      <PopoverBody p={0}>
                        <Fade in={showNoResultsFound} unmountOnExit>
                          <ResultsNotFound
                            searchType={searchBarType}
                            showIncludeManualMedicine={allowManual && search !== ''}
                            searchValue={search}
                            onSelectManualMedicineSearch={onSelectManualMedicineSearch}
                            mandatoryRemumeSearch={mandatoryRemumeSearch}
                            openFormulaFormDialog={handleOpenFormulaFormDialog}
                          />
                        </Fade>
                        <Box id="MenuList" maxHeight={appViewportHeight(0.4)} overflow={'auto'}>
                          <MedicineList medicines={medicines} onSelectMedicine={onSelectMedicineSearch} />
                          <ExamList exams={exams} onSelectExam={onSelectExamSearch} />
                        </Box>
                      </PopoverBody>
                      {searchBarType === SEARCH_TYPE.MEDICINE && (
                        <PopoverFooter
                          border={'none'}
                          bg={'gray.lightest'}
                          minH={'38px'}
                          p={'10px 20px'}
                          borderRadius={'0px 0px 10px 10px'}
                        >
                          {allowManual && search !== '' && !showNoResultsFound && (
                            <Flex
                              justifyContent={'flex-end'}
                              onClick={onSelectManualMedicineSearch}
                              cursor={'pointer'}
                              alignItems={'center'}
                            >
                              <Text color={'#868E96'} fontSize={'12px'}>
                                Incluir medicamento manual
                                <Text as="span" fontSize={'12px'} fontWeight={600}>
                                  {` "${search}" `}
                                </Text>
                              </Text>

                              <Box
                                borderRadius={'50%'}
                                width={'16px'}
                                ml={'10px'}
                                bg={'white'}
                                boxShadow={'3px 3px 10px rgba(61, 14, 107, 0.1);'}
                              >
                                <BsPlusCircle color={'#3484FA'} />
                              </Box>
                            </Flex>
                          )}
                        </PopoverFooter>
                      )}
                    </PopoverContent>
                  </>
                )}
              </Popover>
            )}
          </Box>
        </Box>
      </Box>
      {isConfirmDialogOpen && (
        <ConfirmDialog
          isOpen={isConfirmDialogOpen}
          onClose={() => setIsConfirmDialogOpen(false)}
          onConfirm={handleDeleteFormula}
          onCancel={() => setIsConfirmDialogOpen(false)}
          formulaId={formulaIdToDelete as number}
        />
      )}
    </>
  );
}

export default Search;

interface SearchMedicineParams {
  search: string;
  prescriptionId: number;
  onlyWithBenefits: boolean;
  onlyWithRename: boolean;
  onlyWithRemume: boolean;
}
